// src/features/dashboard/instructores/hooks.js
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "lib/api";
import { useDashboardSettings } from "../settings/store";

const INSTRUCTORES_LIST_PREFIX = ["dashboard", "instructores"];
const INSTRUCTOR_DETAIL_PREFIX = ["dashboard", "instructor"];
const JSON_HEADERS = { "Content-Type": "application/json" };

const dashboardInstructoresKeys = {
  list(periodoId) {
    return ["dashboard", "instructores", { periodoId }];
  },
  detail(periodoId, instructorId) {
    return ["dashboard", "instructor", { periodoId, instructorId }];
  },
  activityOverview(periodoId, instructorId) {
    return ["dashboard", "instructor-activity-overview", { periodoId, instructorId }];
  },
};

function invalidateList(qc) {
  qc.invalidateQueries({ queryKey: INSTRUCTORES_LIST_PREFIX });
}

function invalidateDetail(qc) {
  qc.invalidateQueries({ queryKey: INSTRUCTOR_DETAIL_PREFIX });
}

function invalidateById(qc, periodoId, instructorId) {
  if (!instructorId) return;
  qc.invalidateQueries({ queryKey: dashboardInstructoresKeys.detail(periodoId, instructorId) });
}

// -------------------- Queries --------------------
export function useDashboardInstructores(enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardInstructoresKeys.list(s.periodoId),
    queryFn: () => api("/dashboard/club/instructores", {}, { periodoId: s.periodoId }),
    enabled: enabled && !!s.periodoId,
    staleTime: 20_000,
  });
}

export function useDashboardInstructor(instructorId, enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardInstructoresKeys.detail(s.periodoId, instructorId),
    queryFn: () => api(`/dashboard/club/instructores/${instructorId}`, {}, { periodoId: s.periodoId }),
    enabled: enabled && !!s.periodoId && !!instructorId,
    staleTime: 20_000,
  });
}

export function useDashboardInstructorActivityOverview(instructorId, enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardInstructoresKeys.activityOverview(s.periodoId, instructorId),
    queryFn: () => api(`/dashboard/club/instructores/${instructorId}/actividad-overview`, {}, { periodoId: s.periodoId }),
    enabled: enabled && !!s.periodoId && !!instructorId,
    staleTime: 20_000,
  });
}

// -------------------- Mutations --------------------
export function useCreateDashboardInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) =>
      api(
        "/dashboard/club/instructores",
        { method: "POST", body: JSON.stringify(payload), headers: JSON_HEADERS },
        { periodoId: s.periodoId }
      ),
    onSuccess: () => {
      invalidateList(qc);
    },
  });
}

export function usePatchDashboardInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ instructorId, patch }) =>
      api(
        `/dashboard/club/instructores/${instructorId}`,
        { method: "PATCH", body: JSON.stringify(patch), headers: JSON_HEADERS },
        { periodoId: s.periodoId }
      ),

    // (opcional) optimistic update del detalle si lo usas
    onMutate: async ({ instructorId, patch }) => {
      await qc.cancelQueries({ queryKey: INSTRUCTOR_DETAIL_PREFIX });

      const key = dashboardInstructoresKeys.detail(s.periodoId, instructorId);
      const prev = qc.getQueryData(key);

      if (prev && patch && typeof patch === "object") {
        qc.setQueryData(key, { ...prev, ...patch });
      }

      return { prev, key };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev && ctx?.key) qc.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: () => {
      invalidateList(qc);
      invalidateDetail(qc);
      qc.invalidateQueries({ queryKey: ["dashboard", "context"] });
    },
  });
}

export function useSetDashboardInstructorAvatar() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ instructorId, payload }) =>
      api(
        `/dashboard/club/instructores/${instructorId}/avatar`,
        { method: "POST", body: JSON.stringify(payload), headers: JSON_HEADERS },
        { periodoId: s.periodoId }
      ),
    onSuccess: (_data, vars) => {
      invalidateDetail(qc);
      invalidateList(qc);
      invalidateById(qc, s.periodoId, vars?.instructorId);
    },
  });
}

export function useDeleteDashboardInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (instructorId) =>
      api(`/dashboard/club/instructores/${instructorId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: () => {
      invalidateList(qc);
      qc.removeQueries({ queryKey: INSTRUCTOR_DETAIL_PREFIX });
    },
  });
}

